import { createFileRoute, Link } from "@tanstack/react-router";
import { canonical } from "@/components/site/site-config";
import { CityPageBody, LocalProofCard } from "@/components/site/CityPageBody";
import { BUSINESS } from "@/components/site/constants";
import { ResponsivePicture } from "@/components/site/images";
import {
  BEVERLY_HILLS_SHINGLE_TEAR_OFF,
  BEVERLY_HILLS_SHINGLE_UNDERLAYMENT,
  BEVERLY_HILLS_SHINGLE_IN_PROGRESS,
} from "@/components/site/project-images";

export const Route = createFileRoute("/roofing-company-beverly-hills-fl")({
  head: () => ({
    meta: [
      { title: "Roofing Company in Beverly Hills, FL | Destination Roofing" },
      { name: "description", content: `Local roofing company serving Beverly Hills, FL. Roof repair and replacement. Free Roof Inspection & Estimate. Call ${BUSINESS.phone}.` },
      { property: "og:title", content: "Roofing Company in Beverly Hills, FL" },
      { property: "og:description", content: "Family-owned roofer serving Beverly Hills, FL. Free Roof Inspection & Estimate." },
      { property: "og:url", content: canonical("/roofing-company-beverly-hills-fl") },
    ],
    links: [{ rel: "canonical", href: canonical("/roofing-company-beverly-hills-fl") }],
  }),
  component: () => (
    <CityPageBody
      city="Beverly Hills"
      slug="/roofing-company-beverly-hills-fl"
      localProof={
        <LocalProofCard
          image={BEVERLY_HILLS_SHINGLE_IN_PROGRESS}
          alt="Destination Roofing crew performing shingle roof replacement in Beverly Hills Florida"
          town="Beverly Hills"
          title="Shingle roof replacement in Beverly Hills, FL"
          to="/projects/shingle-roof-replacement-beverly-hills-fl"
        >Tear-off, synthetic underlayment and shingle installation documented stage by stage.</LocalProofCard>
      }
      permitNote={
        <p>
          Roof permits for Beverly Hills homes are handled through the Citrus County Building Division unless the home is inside Crystal River city limits. See our <Link to="/citrus-county-roof-permits">Citrus County roof permit guide</Link>.
        </p>
      }
      intro={
        <p>
          Beverly Hills has a lot of long-time homeowners, many in homes built in the 1970s and 80s. That means we see a mix of aging roofs that have been through multiple Florida storm seasons and are due for either a solid repair or a full replacement.
        </p>
      }
      localNotes={
        <>
          <p>
            Many Beverly Hills roofs we inspect show classic Florida wear: heavy granule loss on south-facing slopes, brittle pipe boots, and worn valleys. On older homes we also often find outdated wind mitigation — which is a real opportunity to lower insurance costs when the roof is replaced.
          </p>
          <p>
            If your home is in one of the older Beverly Hills neighborhoods and it's on its original or second roof, it's worth having us come out and take a look.
          </p>
        </>
      }
      recentProjectSection={
        <>
          <h2>Recent Roof Replacement in Beverly Hills, FL</h2>
          <p>
            Here is a recent roof replacement our crew completed in Beverly Hills. This shingle roof replacement shows our crew working through the tear-off and underlayment stages of the installation. We document the roofing process so homeowners can see what is happening on their roof, not just the finished result.
          </p>
          <figure className="my-6 rounded-xl overflow-hidden border border-border bg-white not-prose">
            <ResponsivePicture
              image={BEVERLY_HILLS_SHINGLE_TEAR_OFF}
              alt="Beverly Hills FL shingle roof replacement tear-off by Destination Roofing"
              sizes="(min-width: 1024px) 66vw, 100vw"
              imgClassName="w-full h-auto"
            />
            <figcaption className="p-3 text-sm text-muted-foreground">Existing shingle roof tear-off in Beverly Hills, FL</figcaption>
          </figure>
          <figure className="my-6 rounded-xl overflow-hidden border border-border bg-white not-prose">
            <ResponsivePicture
              image={BEVERLY_HILLS_SHINGLE_UNDERLAYMENT}
              alt="Synthetic roof underlayment installed during Beverly Hills FL roof replacement"
              sizes="(min-width: 1024px) 66vw, 100vw"
              imgClassName="w-full h-auto"
            />
            <figcaption className="p-3 text-sm text-muted-foreground">Synthetic underlayment installed during a Beverly Hills roof replacement</figcaption>
          </figure>
          <figure className="my-6 rounded-xl overflow-hidden border border-border bg-white not-prose">
            <ResponsivePicture
              image={BEVERLY_HILLS_SHINGLE_IN_PROGRESS}
              alt="Destination Roofing crew performing shingle roof replacement in Beverly Hills Florida"
              sizes="(min-width: 1024px) 66vw, 100vw"
              imgClassName="w-full h-auto"
            />
            <figcaption className="p-3 text-sm text-muted-foreground">Destination Roofing crew working on a Beverly Hills shingle roof replacement</figcaption>
          </figure>
          <p>
            Need to know whether your Beverly Hills roof needs repair or replacement? We offer free <Link to="/roof-inspection">roof inspections</Link>, and same-day or next-day appointments are often available depending on scheduling, weather and roof safety. When a full <Link to="/roof-replacement">roof replacement</Link> is the right call, we'll walk you through the process from tear-off to finished roof. Follow this job stage by stage on the <Link to="/projects/shingle-roof-replacement-beverly-hills-fl">Beverly Hills shingle replacement project page</Link>.
          </p>
        </>
      }
    />
  ),
});
